import React, { createContext, useState, useContext, useEffect } from "react";
import { authService, setTokens, clearTokens, getRefreshToken } from "../services/api";
import { claimPrefsOwner, purgeCachedUserState } from "../config/prefsOwner";
import { clearAll as clearOfflineCache } from "../services/offlineCache";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      loadUserProfile();
    } else {
      setIsLoading(false);
    }
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await authService.getProfile();
      // Identity is settled here first, so this is where a browser that was
      // holding somebody else's cached settings and selected chart is emptied —
      // before any context reads it. Keyed on signing IN, not on the previous
      // person signing out: nobody should have to log out to keep their settings
      // to themselves. See config/prefsOwner.js.
      if (claimPrefsOwner(response.data?.username)) {
        purgeCachedUserState();
        // localStorage only; the offline response cache (§68.8) lives in
        // IndexedDB and has to be dropped by name.
        clearOfflineCache();
      }
      setUser(response.data);
      setError(null);
    } catch (err) {
      console.error("Failed to load profile:", err);
      clearTokens();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (err) => {
    if (typeof err === "string") {
      return err;
    }
    if (err?.response?.data?.detail) {
      const detail = err.response.data.detail;
      if (typeof detail === "string") {
        return detail;
      }
      return JSON.stringify(detail);
    }
    return "An error occurred";
  };

  const login = async (username, password, rememberMe = false) => {
    setIsLoading(true);
    try {
      const response = await authService.login(username, password, rememberMe);
      setTokens(response.data);
      await loadUserProfile();
      return true;
    } catch (err) {
      setError(getErrorMessage(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (credential, rememberMe = true) => {
    setIsLoading(true);
    try {
      const response = await authService.googleLogin(credential, rememberMe);
      setTokens(response.data);
      await loadUserProfile();
      return true;
    } catch (err) {
      setError(getErrorMessage(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (username, email, password, name, rememberMe = false) => {
    setIsLoading(true);
    try {
      const response = await authService.register(username, email, password, name, rememberMe);
      setTokens(response.data);
      await loadUserProfile();
      return true;
    } catch (err) {
      setError(getErrorMessage(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Best-effort: revoke the refresh token server-side so it can't be reused.
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await authService.logout(refreshToken);
      } catch (e) {
        // Ignore — we clear locally regardless.
      }
    }
    clearTokens();
    setUser(null);
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        login,
        loginWithGoogle,
        register,
        logout,
        reloadUser: loadUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
